import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    WatchSignals,
} from "../components/AnalyticsShared";
import { AnalyticsSeriesChart } from "../components/AnalyticsSeriesChart";
import { useAsyncBlock } from "../hooks";
import { bidsFillRateApi } from "../../bidsApi";
import { BidFillRateReport } from "../../../../models/bidAnalytics";
import { formatInt, formatRatePercent, toNum } from "../format";
import { rollupSeries } from "../rollup";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function FillRateTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    const [state, refresh] = useAsyncBlock<BidFillRateReport>(
        () =>
            bidsFillRateApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId]
    );

    const data = state.data?.totals;
    const chartData = React.useMemo(() => {
        const rolled = rollupSeries(state.data?.series ?? [], filters.granularity, {
            sumKeys: [
                "bidsCount",
                "totalRequestedQty",
                "totalBoughtQty",
                "remainingQty",
                "unfilledClosedQty",
            ],
            rateDefs: [
                {
                    rateKey: "volumeFillRate",
                    numerator: "totalBoughtQty",
                    denominator: "totalRequestedQty",
                },
            ],
            weightedAvg: [{ valueKey: "averageBidFillRate", weightKey: "bidsCount" }],
        });
        return rolled.map((p) => ({
            ...p,
            volumeFillPct: toNum(p.volumeFillRate) * 100,
        }));
    }, [state.data?.series, filters.granularity]);

    const isEmpty = data != null && data.bidsCount === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showGranularity
            />

            <AnalyticsCard
                title="Насколько позиции реально выкупаются"
                subtitle="Доля купленных штук от запрошенных. Купленное считается и по ещё открытым заявкам."
                snapshotLabel="period"
                howToRead={
                    <Typography level="body-sm" component="div">
                        <strong>По объёму</strong> — сколько штук выкупили из запрошенных, включая частичный выкуп.
                        Бар — остаток открытых, купленное и закрытое без покупки.
                    </Typography>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Доля исполнения по объёму",
                                ifGrows: "продавцы лучше закрывают позиции, ликвидность улучшилась.",
                                ifFalls: "офферы хуже закрывают спрос (мало продавцов, цены не сходятся, сбои).",
                            },
                            {
                                signal: "Остаток шт.",
                                growsIsGood: false,
                                ifGrows: "копится невыкупленный спрос, в том числе на выключенных заявках.",
                                ifFalls: "остаток сходит в покупки или закрытие.",
                            },
                            {
                                signal: "Закрыто без покупки",
                                growsIsGood: false,
                                ifGrows: "покупатели снимают заявки, не выкупив позиции.",
                                ifFalls: "меньше объёма уходит в отмену.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && isEmpty && <EmptyState />}
                {!state.loading && !state.error && data && !isEmpty && (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <MetricHint label="Заявок" value={formatInt(data.bidsCount)} />
                            <MetricHint
                                label="Доля исполнения по объёму"
                                value={formatRatePercent(data.volumeFillRate)}
                                hint={`${formatInt(toNum(data.totalBoughtQty))} из ${formatInt(toNum(data.totalRequestedQty))} шт.`}
                                color="primary"
                            />
                            <MetricHint
                                label="Куплено шт."
                                value={formatInt(toNum(data.totalBoughtQty))}
                                color="success"
                            />
                            <MetricHint
                                label="Остаток шт."
                                value={formatInt(toNum(data.remainingQty))}
                                color="primary"
                            />
                            <MetricHint
                                label="Закрыто без покупки"
                                value={formatInt(toNum(data.unfilledClosedQty))}
                                color="danger"
                            />
                        </Box>

                        <Typography level="title-sm">Динамика</Typography>
                        <AnalyticsSeriesChart
                            dataset={chartData}
                            granularity={filters.granularity}
                            series={[
                                {
                                    dataKey: "volumeFillPct",
                                    label: "Доля исполнения %",
                                    color: "#1976d2",
                                    valueFormatter: (v) =>
                                        v == null ? "—" : `${v.toLocaleString("ru-RU", { maximumFractionDigits: 1 })}%`,
                                },
                                {
                                    dataKey: "remainingQty",
                                    label: "Остаток",
                                    color: "#1976d2",
                                },
                                {
                                    dataKey: "unfilledClosedQty",
                                    label: "Закрыто без покупки",
                                    color: "#c62828",
                                },
                            ]}
                        />

                        <BarChart
                            height={220}
                            series={[
                                {
                                    data: [
                                        toNum(data.remainingQty),
                                        toNum(data.totalBoughtQty),
                                        toNum(data.unfilledClosedQty),
                                    ],
                                    label: "Штук",
                                    color: "#1976d2",
                                    valueFormatter: (v) => formatInt(v ?? 0),
                                },
                            ]}
                            xAxis={[
                                {
                                    data: ["Остаток", "Куплено", "Закрыто без покупки"],
                                    scaleType: "band",
                                },
                            ]}
                            margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
                        />
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
